import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/ui/Header';
import FixedSidebar from '../../../components/ui/FixedSidebar';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import TaskList from '../../../components/task-management/TaskList';
import TaskForm from '../../../components/task-management/TaskForm';
import TaskStats from '../../../components/task-management/TaskStats';
import authService from '../../../services/authService';
import userManagementService from '../../../services/userManagementService';
import { hasActionAccess } from '../../../utils/roleBasedAccess';

const TaskManagementPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(authService.getStoredUser());
  const [activeTab, setActiveTab] = useState('my-tasks');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Check permissions
  const canViewTasks = hasActionAccess(currentUser?.role, 'tasks', 'view-tasks');
  const canCreateTasks = hasActionAccess(currentUser?.role, 'tasks', 'create-tasks');
  const canManageAllTasks = hasActionAccess(currentUser?.role, 'tasks', 'manage-all-tasks');

  useEffect(() => {
    if (canCreateTasks) {
      loadAvailableUsers();
    }
  }, [canCreateTasks]);

  const loadAvailableUsers = async () => {
    try {
      console.log('[TaskManagement] Loading available users...');
      const response = await userManagementService.getUsers({
        page: 1,
        page_size: 100,
        role: 'hr_coordinator,recruiter,hr_manager,admin' // Now supported by backend
      });
      console.log('[TaskManagement] Users loaded:', response);
      const users = response.users || response.data?.users || [];
      
      // Filter to only include active users
      const activeUsers = users.filter(user => user.is_active);
      
      console.log('[TaskManagement] Available users for assignment:', activeUsers.length);
      setAvailableUsers(activeUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      // Fallback to empty array to prevent blocking the UI
      setAvailableUsers([]);
    }
  };

  // Handle task update
  const handleTaskUpdate = (updatedTask) => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Handle task creation
  const handleTaskCreate = (newTask) => {
    setShowCreateForm(false);
    setRefreshTrigger(prev => prev + 1);
  };

  if (!canViewTasks) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} />
        <FixedSidebar currentPath="/hr-roles/coordinator/tasks" />
        
        <main className="ml-72 pt-16">
          <div className="p-6">
            <div className="text-center py-12">
              <Icon name="Lock" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Access Denied</h3>
              <p className="text-muted-foreground mb-4">
                You don't have permission to view tasks.
              </p>
              <Button onClick={() => navigate('/hr-dashboard')} variant="outline">
                Return to Dashboard
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const tabs = [
    {
      id: 'my-tasks',
      label: 'My Tasks',
      icon: 'User',
      description: 'Tasks assigned to me'
    },
    {
      id: 'all-tasks',
      label: 'All Tasks',
      icon: 'Users',
      description: 'All team tasks',
      disabled: !canManageAllTasks
    },
    {
      id: 'statistics',
      label: 'Statistics',
      icon: 'BarChart3',
      description: 'Task analytics'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} />
      <FixedSidebar currentPath="/hr-roles/coordinator/tasks" />
      
      <main className="ml-72 pt-16">
        <div className="p-6 max-w-7xl">
          <Breadcrumb customBreadcrumbs={[
            { label: 'Coordinator Dashboard', href: '/hr-roles/coordinator' },
            { label: 'Task Management', href: '/hr-roles/coordinator/tasks', isActive: true }
          ]} />

          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Task Management</h1>
              <p className="text-muted-foreground">
                Manage and track your tasks and team assignments
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {canCreateTasks && (
                <Button onClick={() => setShowCreateForm(!showCreateForm)}>
                  <Icon name="Plus" size={16} className="mr-2" />
                  {showCreateForm ? 'Cancel' : 'Create Task'}
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate('/hr-roles/coordinator')}>
                <Icon name="ArrowLeft" size={16} className="mr-2" />
                Back to Dashboard
              </Button>
            </div>
          </div>

          {/* Create Task Form */}
          {showCreateForm && (
            <div className="mb-8">
              <TaskForm
                onSave={handleTaskCreate}
                onCancel={() => setShowCreateForm(false)}
                availableUsers={availableUsers}
              />
            </div>
          )}

          {/* Tabs */}
          <div className="mb-6">
            <div className="border-b border-border">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => !tab.disabled && setActiveTab(tab.id)}
                    disabled={tab.disabled}
                    className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-primary text-primary'
                        : tab.disabled
                        ? 'border-transparent text-muted-foreground cursor-not-allowed opacity-50'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                    }`}
                  >
                    <Icon
                      name={tab.icon}
                      size={16}
                      className={`mr-2 ${
                        activeTab === tab.id
                          ? 'text-primary'
                          : tab.disabled
                          ? 'text-muted-foreground'
                          : 'text-muted-foreground group-hover:text-foreground'
                      }`}
                    />
                    <span>{tab.label}</span>
                    {tab.disabled && (
                      <Icon name="Lock" size={12} className="ml-1 text-muted-foreground" />
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'my-tasks' && (
              <div>
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-foreground mb-2">My Tasks</h2>
                  <p className="text-muted-foreground">
                    Tasks that have been assigned to you
                  </p>
                </div>
                <TaskList
                  showMyTasksOnly={true}
                  onTaskUpdate={handleTaskUpdate}
                  key={`my-tasks-${refreshTrigger}`}
                />
              </div>
            )}

            {activeTab === 'all-tasks' && canManageAllTasks && (
              <div>
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-foreground mb-2">All Tasks</h2>
                  <p className="text-muted-foreground">
                    View and manage all team tasks
                  </p>
                </div>
                <TaskList
                  showMyTasksOnly={false}
                  onTaskUpdate={handleTaskUpdate}
                  key={`all-tasks-${refreshTrigger}`}
                />
              </div>
            )}

            {activeTab === 'statistics' && (
              <div>
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-foreground mb-2">Task Statistics</h2>
                  <p className="text-muted-foreground">
                    Analytics and insights about task performance
                  </p>
                </div>
                
                {/* User's Personal Stats */}
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-foreground mb-4">My Task Statistics</h3>
                  <TaskStats 
                    userId={currentUser?.id} 
                    refreshTrigger={refreshTrigger}
                  />
                </div>

                {/* Team Stats (if user can manage all tasks) */}
                {canManageAllTasks && (
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Team Statistics</h3>
                    <TaskStats 
                      userId={null} 
                      refreshTrigger={refreshTrigger}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Stats Summary at Bottom */}
          {(activeTab === 'my-tasks' || activeTab === 'all-tasks') && (
            <div className="mt-8 pt-6 border-t border-border">
              <h3 className="text-lg font-medium text-foreground mb-4">Quick Overview</h3>
              <TaskStats 
                userId={activeTab === 'my-tasks' ? currentUser?.id : null}
                refreshTrigger={refreshTrigger}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TaskManagementPage;